/**
 * The job form — `docs/record-flyouts.md` §4.3, from
 * `24-record-flyouts/html/flyout-new-job.html`.
 *
 * One form for a job that exists and one that does not, and — new here — one
 * *file*. There used to be a `SlideOver` in `edit-job.tsx` and a page at
 * `/jobs/new` that wrapped it in an `EmptyState` so the screen behind the
 * modal was not blank. The page stays, because a link to "new job" is a thing
 * somebody sends and bookmarks, but it opens this sheet rather than a second
 * form.
 *
 * What is here is what somebody typed: the title, what kind of work it is, and
 * whose it is. A job being created also picks its property, because that is
 * the first question a job answers and nothing else makes sense without it.
 *
 * Status is not here — it moves through the state machine and its buttons, so
 * a job cannot be dragged to Paid by editing a field. Nor is the property,
 * once the job exists: a job at the wrong address is a different job, and
 * changing it would take its visits, quotes and money to a site nobody agreed
 * to. Both of those are re-creations, and the screen should not pretend
 * otherwise by offering a dropdown.
 *
 * **What it no longer asks.** One-time or recurring was a question here and
 * is answered on the quote, by the visits somebody actually builds — so the
 * form stopped asking something it could not know and the quote would
 * overrule anyway.
 *
 * **The note goes on the property, not the job.** What somebody types when
 * they take the call — "wants the beds redone before the Fourth, hates the
 * red mulch" — is true of the *place*, and it is what the next person to
 * quote it needs. A note on a job is read once and buried when the job
 * closes; on the property it is there for the next five jobs. So the box is
 * here, on the form where the call is taken, and the note is written to the
 * property.
 */
import { useEffect, useMemo, useState } from 'react'
import { Sheet } from '../sheet.js'
import { Band, Button } from '../primitives.js'
import {
  Field, FormGrid, FormSection, ReadonlyInput, RecordLookup, SelectInput,
  TextArea, TextInput, Wide,
  type ComboItem,
} from '../form/index.js'
import { request } from '../../lib/api.js'
import { IconCheck, IconFile } from '../../frame/icons.js'

interface Person { id: string; name: string }
/**
 * `code` is what a job stores in `service_line` — the server, the list and
 * every join read `divisions.code` (F3, `docs/new-request.md` §1). The form
 * used to send the name, so a job made here named a division nothing found.
 */
interface Division { id: string; name: string; code: string }

/** A property, with the facts that tell two of the same name apart. */
interface PropertyOption extends ComboItem {
  /**
   * Whose place it is, read back off the row rather than printed on it.
   *
   * It lives in `ComboItem.meta` now that `RecordLookup` builds the rows —
   * the field is kept so a property carried in from elsewhere (an existing
   * job being edited) still fills in the Customer line.
   */
  customer?: string
}

export interface JobRecord {
  id: string
  version: number
  title: string
  serviceLine: string | null
  jobType: string | null
  salespersonId: string | null
}

export function JobForm({
  open, onClose, existing, propertyId, propertyName, onSaved, onSavedAndQuote,
}: {
  open: boolean
  onClose: () => void
  /** Present when editing; absent when adding. */
  existing?: JobRecord | null
  /** Pre-chosen when adding from a property's page. Ignored when editing. */
  propertyId?: string
  /** What to call it, when it was pre-chosen. */
  propertyName?: string
  onSaved: (jobId: string) => void
  /**
   * Create it and go straight to a new quote.
   *
   * The second-commonest thing after "make a job" is "quote it", and the
   * mockup puts it in the footer beside Create rather than making somebody
   * find the button on the record they just made. The form makes the quote
   * itself — it is one call and it knows the job's id — so every caller has
   * to do is say where to land.
   */
  onSavedAndQuote?: (jobId: string, quoteId: string | null) => void
}) {
  const [title, setTitle] = useState('')
  const [serviceLine, setServiceLine] = useState('')
  const [jobType, setJobType] = useState('')
  const [salespersonId, setSalespersonId] = useState<string | null>(null)
  const [note, setNote] = useState('')
  const [people, setPeople] = useState<Person[]>([])
  const [divisions, setDivisions] = useState<Division[]>([])
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [dirty, setDirty] = useState(0)

  // Choosing the property, when there is not one yet.
  const [chosen, setChosen] = useState<PropertyOption | null>(null)

  useEffect(() => {
    if (!open) return
    setTitle(existing?.title ?? '')
    setServiceLine(existing?.serviceLine ?? '')
    setJobType(existing?.jobType ?? '')
    setSalespersonId(existing?.salespersonId ?? null)
    setNote('')
    setChosen(null)
    setProblem(null)
    setErrors({})
    setDirty(0)

    void request<{ items: Person[] }>('/people?limit=200')
      .then((result) => setPeople(result.items))
      .catch(() => setPeople([]))
    void request<{ items: Division[] }>('/catalog-admin/divisions')
      .then((result) => {
        setDivisions(result.items)
        /*
         * A job saved before the fix may still hold the name (0132 rewrote
         * every one it could match); read it back as the code it means, so
         * its division still shows.
         */
        const stored = existing?.serviceLine
        const byName = stored ? result.items.find((one) => one.name === stored) : undefined
        if (byName && !result.items.some((one) => one.code === stored)) {
          setServiceLine(byName.code)
        }
      })
      .catch(() => setDivisions([]))
  }, [open, existing])

  const touch = () => setDirty((count) => count + 1)

  const salesperson = people.find((one) => one.id === salespersonId) ?? null

  /** The people this company has, as the lookup reads them. */
  const searchPeople = useMemo(() => (query: string) => {
    const wanted = query.trim().toLowerCase()
    return people
      .filter((one) => one.name.toLowerCase().includes(wanted))
      .slice(0, 8)
      .map((one) => ({ id: one.id, name: one.name }))
  }, [people])

  async function save(thenQuote: boolean) {
    const found: Record<string, string> = {}
    if (!title.trim()) found.title = 'A job needs a title'
    if (!existing && !propertyId && !chosen) found.property = 'A job happens somewhere'
    setErrors(found)
    if (Object.keys(found).length > 0) return

    setBusy(true)
    setProblem(null)
    try {
      const body = {
        title: title.trim(),
        serviceLine: serviceLine.trim() || null,
        jobType: jobType.trim() || null,
        salespersonId: salespersonId || null,
      }

      if (existing) {
        await request(`/jobs/${existing.id}`, {
          method: 'PATCH',
          body: { ...body, version: existing.version },
        })
        onSaved(existing.id)
        return
      }

      const target = propertyId ?? chosen?.id
      if (!target) return
      const created = await request<{ id: string }>('/jobs', {
        method: 'POST',
        body: { ...body, propertyId: target },
      })

      /*
       * The note goes on the property and names the job, so it is findable
       * from either. If it fails the job still exists and is still right —
       * losing the job because a note would not save would be the worse
       * trade — so this does not throw.
       */
      if (note.trim()) {
        await request(`/records/property/${target}/activities`, {
          method: 'POST',
          body: {
            kind: 'note',
            title: `Asked for — ${body.title}`,
            body: note.trim(),
            visibility: 'internal',
            links: [{ object: 'job', id: created.id }],
          },
        }).catch(() => undefined)
      }

      if (thenQuote && onSavedAndQuote) {
        /*
         * If the quote cannot be made the job still exists, so hand back a
         * null rather than an error: the caller lands on the job, which is
         * true, instead of on a screen claiming nothing happened.
         */
        const quote = await request<{ id: string }>('/quotes', {
          method: 'POST', body: { jobId: created.id },
        }).catch(() => null)
        onSavedAndQuote(created.id, quote?.id ?? null)
        return
      }
      onSaved(created.id)
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  const site = chosen?.name ?? propertyName ?? null
  const customer = chosen?.meta?.accountName ?? chosen?.customer ?? null

  return (
    <Sheet
      open
      onClose={onClose}
      kind="job"
      title={existing ? (title || 'Job') : 'New job'}
      sub="Jobs · a request that will get a quote"
      dirty={dirty > 0}
      changeCount={dirty}
      saveLabel={existing ? 'Save job' : 'Create job'}
      onSave={() => void save(false)}
      note={Object.keys(errors).length > 0
        ? (
          <span className="text-danger">
            {Object.keys(errors).length === 1
              ? 'One field needs attention'
              : `${Object.keys(errors).length} fields need attention`}
          </span>
        )
        : existing
          ? 'the status moves with the buttons on the job, not here'
          : undefined}
      secondary={!existing && onSavedAndQuote
        ? (
          <Button className="h-[34px]" disabled={busy} onClick={() => void save(true)}>
            <IconFile size={15} />Create &amp; start a quote
          </Button>
        )
        : undefined}
      primary={(
        <Button
          variant="primary"
          className="h-[34px]"
          disabled={busy}
          onClick={() => void save(false)}
        >
          <IconCheck size={15} />
          {busy ? 'Saving…' : existing ? 'Save job' : 'Create job'}
        </Button>
      )}
    >
      {problem && <Band tone="amber">{problem}</Band>}

      {/*
        * Where it is, and only while it is still a question. A job is one
        * piece of work for one property, so this is the first thing it
        * answers — and the last thing it is allowed to change its mind
        * about, which is why the section is gone once the job exists.
        */}
      {!existing && (
        <>
          <FormSection title="Where and for whom" />
          <FormGrid>
            <Wide>
              {propertyId
                ? (
                  <Field label="Property" hint="chosen from where you came in">
                    {({ id, describedBy }) => (
                      <ReadonlyInput
                        id={id}
                        describedBy={describedBy}
                        value={site ?? 'This property'}
                      />
                    )}
                  </Field>
                )
                : (
                  <Field label="Property" required error={errors.property}>
                    {({ id, describedBy, invalid }) => (
                      <RecordLookup
                        id={id}
                        describedBy={describedBy}
                        invalid={invalid}
                        kind="property"
                        label="Property"
                        placeholder="Search properties…"
                        value={chosen}
                        onPick={(picked) => {
                          setChosen(picked as PropertyOption)
                          setErrors((current) => Object.fromEntries(
                            Object.entries(current).filter(([key]) => key !== 'property')))
                          touch()
                        }}
                      />
                    )}
                  </Field>
                )}
            </Wide>

            {customer && (
              <Wide>
                <Field label="Customer" hint="from the property">
                  {({ id, describedBy }) => (
                    <ReadonlyInput id={id} describedBy={describedBy} value={customer} />
                  )}
                </Field>
              </Wide>
            )}
          </FormGrid>
        </>
      )}

      <FormSection title="What" />
      <FormGrid>
        <Wide>
          <Field label="Title" required error={errors.title}>
            {({ id, describedBy, invalid }) => (
              <TextInput
                id={id}
                describedBy={describedBy}
                invalid={invalid}
                value={title}
                placeholder="Fall cleanup and bed edging"
                onChange={(event) => { setTitle(event.target.value); touch() }}
              />
            )}
          </Field>
        </Wide>

        <Field label="Division" hint="the crews and the catalog this job draws from">
          {({ id, describedBy }) => (
            <SelectInput
              id={id}
              describedBy={describedBy}
              label="Division"
              value={serviceLine || null}
              placeholder="Any"
              clearable
              clearLabel="Any"
              onChange={(value) => { setServiceLine(value ?? ''); touch() }}
              options={divisions.map((one) => ({ value: one.code, label: one.name }))}
            />
          )}
        </Field>

        <Wide>
          {/*
            * A lookup, not a dropdown — the same control as the property
            * above it. A company with sixty people in it cannot be read off
            * a list, and half this form is already something you search.
            */}
          <Field label="Salesperson" hint="who owns it, and whose number it lands in">
            {({ id, describedBy }) => (
              <RecordLookup
                id={id}
                describedBy={describedBy}
                kind="user"
                label="Salesperson"
                placeholder="Search people…"
                value={salesperson ? { id: salesperson.id, name: salesperson.name } : null}
                onPick={(picked) => { setSalespersonId(picked.id); touch() }}
                /*
                 * Every person in the company is already in memory here, so
                 * the filter is local; the lookup still puts whoever this
                 * user last picked at the top. Inviting somebody is not a
                 * thing to do halfway through writing a job, so no create row.
                 */
                search={searchPeople}
                allowCreate={false}
              />
            )}
          </Field>
        </Wide>
        {!existing && (
          <Wide>
            <Field
              label="What they asked for"
              optional
              hint="goes on the property as its first note — the next person to quote it reads this"
            >
              {({ id, describedBy }) => (
                <TextArea
                  id={id}
                  describedBy={describedBy}
                  rows={4}
                  value={note}
                  placeholder={'Beds along the drive want redoing before the Fourth. '
                    + 'Hates the red mulch. Gate code is on the property already.'}
                  onChange={(event) => { setNote(event.target.value); touch() }}
                />
              )}
            </Field>
          </Wide>
        )}
      </FormGrid>

      <p className="m-0 text-[12px] leading-[1.45] text-text-muted">
        {existing
          ? 'The property cannot be changed at all — a job somewhere else is a new job, '
            + 'and moving this one would take its visits, quotes and money with it.'
          : 'It starts as Requested and is numbered when it saves. Quote it, and the board '
            + 'moves it along.'}
      </p>
    </Sheet>
  )
}
